import { Injectable } from '@angular/core';
import { MemoizedSelector, Store, select } from '@ngrx/store';
import { EMPTY, Observable, finalize, map, of, shareReplay, switchMap, take } from 'rxjs';
import { TypedCacheBucket } from '../models/state.models';
import { asTypedCacheBucket, findCacheEntry, upsertCacheEntry } from '../utils/typed-cache';
import { RootReducerState } from '../reducers';

@Injectable({ providedIn: 'root' })
export class StateCacheFetchService {
  constructor(private readonly store: Store<RootReducerState>) {}

  /**
   * Requests already in flight, keyed by selector (and by `type` for typed buckets). Several
   * components can call the same `redux.getXxx()` on the same tick — before any of them has
   * persisted a response back to the store, so the plain "already cached?" check below can't see
   * each other. Without this, every one of them fires its own HTTP request (this is what showed
   * up as N duplicate `/general/tag-settings` calls in one page load). Sharing the in-flight
   * observable via shareReplay collapses those into a single request; the entry is removed once
   * it settles so later calls just read the persisted store value instead.
   */
  private readonly scalarInFlight = new Map<MemoizedSelector<any, any>, Observable<any>>();
  private readonly bucketInFlight = new Map<MemoizedSelector<any, any>, Map<string, Observable<any>>>();

  /**
   * When each cached value was last fetched from the server, so `force` can mean "refetch if it's
   * gone stale" rather than "refetch unconditionally".
   *
   * The in-flight map above only collapses calls that overlap. It does nothing for the far more
   * common shape measured in production (FRONTEND-DUPLICATE-API-CALLS-2026-09-17.md §3): the same
   * office-level config fetched again every time a module mounts, 30-90 seconds apart, because 50
   * of the 173 `getTags()` call sites pass `force = true`. Those requests don't overlap, so they
   * each cost a full round trip — five identical 161 KB `tag-settings` responses in three minutes,
   * 645 KB of the 1.69 MB wasted in that session.
   *
   * Rewriting those 50 call sites individually would mean judging, one by one, which genuinely
   * needs fresh data. A freshness window fixes all of them at once and keeps the guarantee they
   * were written for, provided the cache is invalidated when the data actually changes — which is
   * what {@link invalidate} is for. Without that invalidation this would be unsafe: an author who
   * edits a tag and expects to see it must not be served the pre-edit copy.
   */
  private readonly lastFetchedAt = new Map<MemoizedSelector<any, any>, Map<string, number>>();

  /** Key used for scalar values, which have no `type` dimension of their own. */
  private static readonly SCALAR_KEY = '__scalar__';

  private isFresh(selector: MemoizedSelector<any, any>, key: string, freshMs?: number): boolean {
    if (!freshMs || freshMs <= 0) {
      return false;
    }
    const at = this.lastFetchedAt.get(selector)?.get(key);
    return at != null && Date.now() - at < freshMs;
  }

  private stamp(selector: MemoizedSelector<any, any>, key: string): void {
    let byKey = this.lastFetchedAt.get(selector);
    if (!byKey) {
      byKey = new Map<string, number>();
      this.lastFetchedAt.set(selector, byKey);
    }
    byKey.set(key, Date.now());
  }

  /**
   * Drops the freshness stamp so the next `force` fetch really does go to the server.
   *
   * Call this whenever the underlying data has been changed — a tag saved or deleted, an office
   * switched, a logout. Omit `key` to invalidate every entry under that selector. This only clears
   * the timestamp, never the cached value: a caller that isn't forcing still reads the store, so
   * nothing blanks out while the refetch is in flight.
   */
  invalidate(selector: MemoizedSelector<any, any>, key?: string): void {
    if (key == null) {
      this.lastFetchedAt.delete(selector);
      return;
    }
    this.lastFetchedAt.get(selector)?.delete(key);
  }

  /** Invalidates everything — for an office switch or logout, where all of it is now another
   *  office's data. */
  invalidateAll(): void {
    this.lastFetchedAt.clear();
  }

  /** Read-through cache for a single store value (user, offices, notes). */
  fetchScalar<T>(options: {
    selector: MemoizedSelector<RootReducerState, T>;
    force: boolean;
    hasAuth: () => boolean;
    request: () => Observable<T>;
    persist: (value: T) => void;
    isCached: (value: T) => boolean;
    /** Serve the cache even when forced, if it was fetched less than this long ago. */
    freshMs?: number;
  }): Observable<T> {
    return this.store.pipe(
      select(options.selector),
      take(1),
      switchMap(cached => {
        if (options.isCached(cached) && (!options.force || this.isFresh(options.selector, StateCacheFetchService.SCALAR_KEY, options.freshMs))) {
          return of(cached);
        }
        if (!options.hasAuth()) {
          return EMPTY;
        }

        const existing = this.scalarInFlight.get(options.selector);
        if (existing) {
          return existing;
        }

        const request$ = options.request().pipe(
          map(response => {
            options.persist(response);
            this.stamp(options.selector, StateCacheFetchService.SCALAR_KEY);
            return response;
          }),
          shareReplay(1),
          finalize(() => this.scalarInFlight.delete(options.selector))
        );
        this.scalarInFlight.set(options.selector, request$);
        return request$;
      })
    );
  }

  /** Read-through cache keyed by `type` within a typed bucket (tags, project info, contacts). */
  fetchTypedBucket<TResponse>(options: {
    selector: MemoizedSelector<RootReducerState, TypedCacheBucket<TResponse>>;
    type: string;
    force: boolean;
    hasAuth: () => boolean;
    request: () => Observable<TResponse>;
    persist: (bucket: TypedCacheBucket<TResponse>) => void;
    /** Serve the cache even when forced, if this `type` was fetched less than this long ago. */
    freshMs?: number;
  }): Observable<TResponse> {
    return this.store.pipe(
      select(options.selector),
      take(1),
      switchMap(cache => {
        const bucket = asTypedCacheBucket(cache);
        const entry = findCacheEntry(bucket, options.type);

        if (entry?.response != null && (!options.force || this.isFresh(options.selector, options.type, options.freshMs))) {
          return of(entry.response);
        }

        if (!options.hasAuth()) {
          return EMPTY;
        }

        let typeMap = this.bucketInFlight.get(options.selector);
        const existing = typeMap?.get(options.type);
        if (existing) {
          return existing;
        }

        const request$ = options.request().pipe(
          map(response => {
            if (response != null) {
              const updated = upsertCacheEntry(bucket, options.type, response, options.force);
              options.persist(updated);
              this.stamp(options.selector, options.type);
            }
            return response;
          }),
          shareReplay(1),
          finalize(() => this.bucketInFlight.get(options.selector)?.delete(options.type))
        );

        if (!typeMap) {
          typeMap = new Map<string, Observable<TResponse>>();
          this.bucketInFlight.set(options.selector, typeMap);
        }
        typeMap.set(options.type, request$);

        return request$;
      })
    );
  }
}
